import { electron } from 'lib-shared-electron';
import { ampli, IdentifyProperties } from 'lib-shared-tracking';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from 'react';
import { useMount } from 'react-use';

import {
  getLocalSettings,
  updateSettings,
} from 'components/settings/api/personal';
import { useServerStorage } from 'utils/useServerStorage';
import { useCurrentUser } from 'utils/user';

import {
  ColorMode,
  ColorModeContextValue,
  ColorModeProviderProps,
  PreferredColorMode,
} from './colorMode.types';

const localStorageKey = 'colorMode';

const ColorModeContext = createContext<ColorModeContextValue>(undefined!);

export function ColorModeProvider({
  children,
}: ColorModeProviderProps): JSX.Element {
  const storage = useServerStorage('user');
  const { user } = useCurrentUser();

  const isColorModeSupported = useMemo(() => !electron.isElectron(), []);

  const prefersDarkMode = useCallback(() => {
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  }, []);

  /**
   * The current color mode preference, resolved either from serverStorage or
   * localStorage.
   */
  const preferredColorMode: PreferredColorMode = isColorModeSupported
    ? (storage.data.global?.colorMode ??
      getLocalSettings()?.[localStorageKey] ??
      'light')
    : 'light';

  /**
   * The effective color mode, which if the user chose 'auto', will be
   * determined by the system's color scheme preference.
   */
  const colorMode = useMemo(
    (): ColorMode =>
      preferredColorMode === 'auto'
        ? prefersDarkMode()
          ? 'dark'
          : 'light'
        : preferredColorMode,
    [preferredColorMode, prefersDarkMode],
  );

  useMount(() => {
    applyPreferredColorMode(preferredColorMode);

    // Ensures that the value in localStorage is in sync with the serverStorage.
    const localPreferredColorMode = getLocalSettings()?.[localStorageKey];
    if (localPreferredColorMode !== preferredColorMode) {
      updateSettings({ key: localStorageKey, value: preferredColorMode });
    }
  });

  // Updates the user's color mode in Amplitude whenever it changes.
  useEffect(() => {
    ampli.identify(user.id, { colorMode } as IdentifyProperties);
  }, [colorMode, user.id]);

  /**
   * Applies the specified color mode to the document's HTML element. If the
   * mode is set to 'auto', it checks the system's color scheme preference and
   * applies either 'dark' or 'light' mode accordingly.
   */
  const applyPreferredColorMode = useCallback(
    (mode: PreferredColorMode) => {
      const colorMode =
        mode === 'auto' ? (prefersDarkMode() ? 'dark' : 'light') : mode;

      document.documentElement.setAttribute('data-color-mode', colorMode);
    },
    [prefersDarkMode],
  );

  // Listens to changes in the system's color scheme preference and updates
  // the color mode accordingly. Only relevant if the user has selected 'auto'.
  useEffect(() => {
    if (!isColorModeSupported) return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const handleChange = (event: MediaQueryListEvent) => {
      applyPreferredColorMode(event.matches ? 'dark' : 'light');
    };

    if (preferredColorMode === 'auto') {
      mediaQuery.addEventListener('change', handleChange);
    }

    return () => {
      mediaQuery.removeEventListener('change', handleChange);
    };
  }, [applyPreferredColorMode, preferredColorMode, isColorModeSupported]);

  /**
   * Previews a specified color mode by applying it to the document's HTML
   * element. This is useful for providing a live preview of the selected mode
   * before the user confirms their choice.
   */
  const previewPreferredColorMode = useCallback(
    (mode: PreferredColorMode) => {
      applyPreferredColorMode(mode);
    },
    [applyPreferredColorMode],
  );

  /**
   * Persists the specified color mode by updating the serverStorage and
   * localStorage. This ensures that the user's preference is saved and applied
   * across sessions.
   */
  const persistPreferredColorMode = useCallback(
    (mode: PreferredColorMode) => {
      applyPreferredColorMode(mode);

      updateSettings({ key: localStorageKey, value: mode });

      storage.set({
        global: (draft) => {
          draft.colorMode = mode;
        },
      });
    },
    [applyPreferredColorMode, storage],
  );

  /** Toggles the color mode between 'dark' and 'light'. */
  const toggleColorMode = useCallback((): ColorMode => {
    const newColorMode: ColorMode = colorMode === 'dark' ? 'light' : 'dark';
    persistPreferredColorMode(newColorMode);
    return newColorMode;
  }, [colorMode, persistPreferredColorMode]);

  const contextValue: ColorModeContextValue = useMemo(
    () => ({
      colorMode,
      preferredColorMode,
      isColorModeSupported,
      persistPreferredColorMode,
      previewPreferredColorMode,
      toggleColorMode,
    }),
    [
      colorMode,
      preferredColorMode,
      isColorModeSupported,
      persistPreferredColorMode,
      previewPreferredColorMode,
      toggleColorMode,
    ],
  );

  return (
    <ColorModeContext.Provider value={contextValue}>
      {children}
    </ColorModeContext.Provider>
  );
}

export function useColorMode(): ColorModeContextValue {
  return useContext(ColorModeContext);
}
